<template>
  <div class="auth-page" :style="clientColor ? { '--brand-color': clientColor } : null">
    <header class="top-bar">
      <div class="brand-pill" :style="{ backgroundColor: logoBackgroundColor }">
        <img
          v-if="clientLogo"
          :src="clientLogo"
          alt="Client Logo"
          class="brand-image" />
        <img
          v-else
          src="@/../public/static/img/acrew-logo-with-effects-white.png"
          alt="ACREW Logo"
          class="brand-image default" />
      </div>
      <div class="top-login">
        <img src="@/../public/static/img/acrew-dash-pattern.png" alt="" class="dash-pattern" />
        <span>Login</span>
      </div>
    </header>
    <div class="content-area">
      <div class="card-wrapper">
        <div class="auth-card">
          <div class="heading">
            <div class="heading-text">
              <h2>Let's get you signed up.</h2>
            </div>
          </div>
          <v-stepper v-model="currentStep">
            <v-stepper-items>
              <v-stepper-content step="0">
                <p class="step-description">Select your dental facility</p>
                <div class="step-container">
                  <form class="signup-form" @submit.prevent="onSubmit">
                    <v-select :items="clientList"
                              :item-text="client => client.name"
                              :item-value="client => client.id"
                              label="Dental Facility"
                              return-object
                              @input="selectClient($event)"
                              dense
                              hide-details="auto"
                              class="line-input custom-filled"
                              :menu-props="{ offsetY: true, maxHeight: 300 }" />
                  </form>
                </div>
              </v-stepper-content>
              <v-stepper-content step="1">
                <p class="step-description">Please use the information you have on file with your dentist.</p>
                <div class="step-container">
                  <div v-if="searchingForPatient" class="loading-step-overlay">
                    <v-progress-circular indeterminate color="primary" size="50" />
                  </div>
                  <v-form ref="searchForm" class="signup-form modern-form" @submit.prevent="searchForPatient">
                  <div class="form-row form-row--stack-on-mobile">
                    <div class="form-row__col">
                      <v-text-field v-model="form.firstName"
                                    :rules="[rules.name]"
                                    validate-on-blur
                                    label="First Name"
                                    type="name"
                                    required
                                    dense
                                    hide-details="auto"
                                    class="line-input custom-filled" />
                    </div>
                    <div class="form-row__col">
                      <v-text-field v-model="form.lastName"
                                    :rules="[rules.name]"
                                    validate-on-blur
                                    label="Last Name"
                                    type="name"
                                    required
                                    dense
                                    hide-details="auto"
                                    class="line-input custom-filled" />
                    </div>
                  </div>
                  <LessBadBirthDatePicker v-model="form.birthDate"
                                          dense
                                          hide-details="auto"
                                          class="line-input" />
                  <v-text-field v-model="form.email"
                                :rules="[rules.email]"
                                validate-on-blur
                                label="Email"
                                type="email"
                                required
                                dense
                                hide-details="auto"
                                class="line-input custom-filled" />
                  <v-text-field v-model="form.phone"
                                v-mask="'(###) ###-####'"
                                :rules="[rules.phone]"
                                validate-on-blur
                                label="Phone"
                                type="tel"
                                required
                                dense
                                hide-details="auto"
                                class="line-input custom-filled" />
                <v-btn class="btn-primary full-width-btn" type="submit" :disabled="searchingForPatient">
                  Find Patient
                </v-btn>
                  </v-form>
                </div>
              </v-stepper-content>
              <v-stepper-content step="2">
                <p class="step-description">We found several possible matches.</p>
                <div class="step-container">
                  <div v-if="sendingMultiFactor" class="loading-step-overlay">
                    <v-progress-circular indeterminate color="primary" size="50" />
                  </div>
                  <form>
                    <div class="patient-match-container" v-for="(patient, i) in potentialPatientMatches" :key="i">
                      <v-card @click="selectPatient(patient)"
                              outlined
                              class="patient-card">
                        <h3>{{ patient.firstName }} {{ patient.lastName }}</h3>
                        {{ patient.age }}
                      </v-card>
                    </div>
                    <p>Don't see your account? Try entering your information again, or call the {{ clientName }} office</p>
                    <v-btn class="btn-secondary full-width-btn" @click="currentStep = 1">Back to Search</v-btn>
                  </form>
                </div>
              </v-stepper-content>
              <v-stepper-content step="3">
                <div class="step-container">
                  <div v-if="sendingMultiFactor" class="loading-step-overlay">
                    <v-progress-circular indeterminate color="primary" size="50" />
                  </div>
                  <h3>Please check your {{ contactMethod }} for a one time use code</h3>
                  <form @submit.prevent="completePatientValidation">
                    <v-otp-input v-model="otpInput"
                                 length="4"
                                 type="tel"
                                 @finish="completePatientValidation" />
                    <p id="otp-helper" class="sr-only">Enter the 4 digit code sent to your {{ contactMethod }}.</p>
                    <div>
                      <div class="helper-section">
                        <span>Need a new code? </span>
                        <v-btn class="btn-outline-primary" type="button" :disabled="sendingMultiFactor" @click="resendPatientValidation()">Resend code</v-btn>
                      </div>
                <v-btn class="btn-primary full-width-btn" @click="completePatientValidation">
                  Submit
                </v-btn>
                    </div>
                  </form>
                </div>
              </v-stepper-content>
              <v-stepper-content step="4">
                <p class="step-description">Set your password to complete signup.</p>
                <div class="step-container">
                  <div v-if="creatingAccount" class="loading-step-overlay">
                    <v-progress-circular indeterminate color="primary" size="50" />
                  </div>
                  <v-form ref="form" class="modern-form" @submit.prevent="checkForm">
                    <div class="form-row form-row--stack-on-mobile">
                      <div class="form-row__col">
                        <v-text-field v-model="form.firstName"
                                      label="First Name"
                                      class="line-input custom-filled"
                                      disabled
                                      dense
                                      hide-details="auto" />
                      </div>
                      <div class="form-row__col">
                        <v-text-field v-model="form.lastName"
                                      label="Last Name"
                                      class="line-input custom-filled"
                                      disabled
                                      dense
                                      hide-details="auto" />
                      </div>
                    </div>
                    <LessBadBirthDatePicker v-model="form.birthDate"
                                            disabled
                                            dense
                                            hide-details
                                            class="line-input" />
                    <v-text-field v-model="form.email"
                                  label="Email"
                                  class="line-input custom-filled"
                                  disabled
                                  dense
                                  hide-details="auto" />
                    <v-text-field :value="form.phone || ' '"
                                  label="Phone"
                                  class="line-input custom-filled"
                                  type="tel"
                                  disabled
                                  dense
                                  hide-details="auto" />
                    <v-text-field v-model="form.password"
                                  ref="passwordField"
                                  label="Password"
                                  type="password"
                                  :rules="[rules.required, rules.pass_all, rules.max50]"
                                  validate-on-blur
                                  required
                                  class="line-input custom-filled"
                                  dense
                                  hide-details="auto" />
                    <v-text-field v-model="form.confirmPassword"
                                  label="Repeat Password"
                                  class="line-input custom-filled"
                                  type="password"
                                  :rules="[
                                    rules.required,
                                    rules.pass_all,
                                    rules.max50,
                                    mustMatch,
                                  ]"
                                  validate-on-blur
                                  required
                                  dense
                                  hide-details="auto" />
                    <div class="button-row">
                      <v-btn class="btn-primary"
                             type="submit"
                             :disabled="creatingAccount">
                        Create Account
                      </v-btn>
                      <v-btn class="btn-secondary" @click="currentStep = 1" :disabled="creatingAccount">
                        Back to Search
                      </v-btn>
                    </div>
                    <p class="terms-note">By signing up for Acrew, I agree to receive SMS messages related to my account, appointments, billing, and care updates. Message frequency varies. Message and data rates may apply. Reply STOP to opt out. Reply HELP for assistance. I agree to the <a href="https://acrewdental.com/terms-of-service/" target="_blank">Terms of Service</a> and <a href="https://acrewdental.com/privacy-policy/" target="_blank">Privacy Policy</a>.</p>
                  </v-form>
                </div>
              </v-stepper-content>
            </v-stepper-items>
          </v-stepper>
        </div>
      </div>    
    </div>
    <div v-if="clientColor" class="powered-by">
      <span>BROUGHT TO YOU BY</span>
      <img src="@/../public/static/img/acrew-logo-with-effects.png" alt="Logo" />
    </div>
  </div>
</template>
<style scoped lang="scss">
  /** NewAccount.vue */
  @import '@/scss/variables.scss';
  @import '@/scss/auth/page.scss';

  .v-stepper__content {
    padding: 0 !important;
  }

  .loading-spinner {
    position: fixed;
    top: 50%;
    left: 0%;
    height: 50px !important;
    margin: auto;
    width: 100%;
    z-index: 99;
  }

  .step-container {
    position: relative;

    .loading-step-overlay {
      position: absolute;
      top: -20px;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(255, 255, 255, 0.9);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 10;
    }
  }

  .patient-match-container {
    margin-bottom: 8px;
    display: block;
  }

  .patient-card {
    width: 300px;
    cursor: pointer;
    border-width: 4px;
    padding: 32px 16px;
    transition: border-color 0.2s ease, box-shadow 0.2s ease;
    
    &:hover {
      border-color: #386ea3 !important;
      box-shadow: 0 4px 12px rgba(56, 110, 163, 0.2);
    }
  }

  .helper-section {
    margin-bottom: 16px;
    display: flex;
    align-items: center;
    gap: 8px;

    @media (max-width: 500px) {
      flex-direction: column;
      text-align: center;
      gap: 12px;
      
      .btn-outline-primary {
        width: 100%;
      }
    }
  }

  .form-row {
    display: flex;
    width: 100%;
    gap: 16px;
    margin-bottom: 0;

    &__col {
      flex: 1 1 0;
      min-width: 0;

      .line-input {
        margin-bottom: 0;
      }
    }

    &--stack-on-mobile {
      @media (max-width: 600px) {
        flex-direction: column;
        gap: 0;

        .form-row__col .line-input {
          margin-bottom: 22px;
        }
      }
    }
  }

  .terms-note {
    margin-top: 16px;
    font-size: 14px;
    color: #666;
    text-align: left;
    line-height: 1.4;
  }

  .otp-container {
    display: flex;
    justify-content: center;

    .otp-input {
      max-width: 400px;
    }
  }

  .full-width-btn {
    width: 100%;
    margin-top: 5px;
  }

  h2, h4 {
    text-align: left;
    margin-left: 0;
  }

  p {
    &.step-description {
      margin-bottom: 16px;
    }
  }
</style>
<script>
import DataService from '../../_services/DataService';
import ValidateService from '../../_services/ValidateService';
import { ALERT_TIMEOUT_MS, loadClientPreferences } from '../utils/UxUtils';

import LessBadBirthDatePicker from '../components/LessBadBirthDatePicker';

export default {
  components: {
    LessBadBirthDatePicker,
  },
  data() {
    return {
      hadClientId: false,
      clientId: '',
      clientLogo: null,
      clientColor: null,
      whiteBehindLogo: false,
      clientName: null,
      currentStep: 1,
      searchingForPatient: false,
      sendingMultiFactor: false,
      creatingAccount: false,
      redirecting: false,
      patientValidationToken: null,
      contactMethod: null,
      otpInput: '',
      potentialPatientMatches: [],
      patientIdToLink: null,
      userAuthToken: null,
      form: {
        password: '',
        confirmPassword: '',
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        birthDate: '',
        address1: '',
        address2: '',
        city: '',
        state: '',
        postalCode: '',
        referralSource: null,
        referralNote: '',
      },
      otherSelected: false,
      rules: ValidateService.validators,
      errors: [],
      referralList: [],
      clientList: [],
    };
  },
  computed: {
    logoBackgroundColor() {
      return this.whiteBehindLogo ? 'white' : (this.clientColor || '#11405c');
    },
  },
  watch: {
    'form.referralSource': {
      handler(newVal) {
        if (!newVal) {
          this.otherSelected = false;
        } else {
          const selectedItem = this.referralList.find(item => item.id === newVal);
          this.otherSelected = selectedItem?.name?.match(/\bother\b/i) || false;
        }
      },
      immediate: true,
    },
  },
  methods: {
    mustMatch() {
      return this.form.password === this.form.confirmPassword ? true : 'Passwords do not match.';
    },
    goToLogin() {
      this.$router.push('/login');
    },
    async selectClient(clientSelectItem) {
      try {
        const clientId = clientSelectItem.value;
        DataService.updateClientId(clientId);
        this.clientId = clientId;
        
        await Promise.all([
          loadClientPreferences(this),
          this.getClientReferralMethods(),
        ]);
        
        this.currentStep = 1;
      } catch (error) {
        this.$root.$emit('raise_alert', { color: 'error', text: 'Failed to load client information. Please try again.' });
      }
    },
    async searchForPatient() {
      if (this.searchingForPatient) return;
      
      if (!this.$refs.searchForm || !this.$refs.searchForm.validate()) {
        return;
      }
      
      this.searchingForPatient = true;
      
      try {
        const searchData = {
          firstName: this.form.firstName,
          lastName: this.form.lastName,
          email: this.form.email,
          phoneNumber: this.form.phone,
          birthDate: this.form.birthDate,
        };
        
        const response = await DataService.searchForPatient(this.clientId, searchData);
        
        if (response.status === 200) {
          this.handlePatientSearchResults(response.data);
        } else {
          this.$root.$emit('raise_alert', { color: 'error', text: response.data || 'An error occurred trying to search for a patient; please try again' });
        }
      } catch (error) {
        this.$root.$emit('raise_alert', { color: 'error', text: 'Network error. Please check your connection and try again.' });
      } finally {
        this.searchingForPatient = false;
      }
    },
    async selectPatient(patient) {
      if (this.sendingMultiFactor) return;
      
      this.sendingMultiFactor = true;
      
      try {
        this.patientIdToLink = patient.patientId;
        await this.beginPatientValidation();
        this.currentStep = 3;
      } catch (error) {
        this.$root.$emit('raise_alert', { color: 'error', text: 'Failed to process patient selection. Please try again.' });
        this.sendingMultiFactor = false;
      }
    },
    async beginPatientValidation() {
      this.otpInput = '';
      
      try {
        const response = await DataService.getPatientValidationToken(this.clientId, this.patientIdToLink);
        
        if (response.status === 200) {
          this.patientValidationToken = response.data.token;
          this.contactMethod = response.data.sentSms ? 'text messages' : 'email';
        } else {
          throw new Error('Failed to send validation request');
        }
      } catch (error) {
        this.$root.$emit('raise_alert', { color: 'error', text: 'An error occurred trying to send a validation request' });
        throw error;
      } finally {
        this.sendingMultiFactor = false;
      }
    },
    async resendPatientValidation() {
      if (this.sendingMultiFactor) return;
      
      this.sendingMultiFactor = true;
      
      try {
        const response = await DataService.getPatientValidationToken(this.clientId, this.patientIdToLink);
        
        if (response.status === 200) {
          this.patientValidationToken = response.data.token;
          this.contactMethod = response.data.sentSms ? 'text messages' : 'email';
          this.$root.$emit('raise_alert', { color: 'success', text: 'A new code has been sent' });
        } else {
          throw new Error('Failed to resend code');
        }
      } catch (error) {
        this.$root.$emit('raise_alert', { color: 'error', text: 'Failed to send code. Please try again.' });
      } finally {
        this.sendingMultiFactor = false;
      }
    },
    async completePatientValidation() {
      if (this.sendingMultiFactor) return;
      
      this.sendingMultiFactor = true;
      
      try {
        const response = await DataService.verifyPatientValidationToken(
          this.clientId,
          this.patientIdToLink,
          this.patientValidationToken,
          this.otpInput,
        );
        
        if (response.status === 200) {
          this.userAuthToken = response.data.token;
          this.form.patientId = response.data.patientId;
          this.form.password = '';
          this.form.confirmPassword = '';
          this.currentStep = 4;
          
          this.$nextTick(() => {
            this.$refs.passwordField?.focus();
          });
        } else {
          this.otpInput = '';
          this.$root.$emit('raise_alert', { color: 'error', text: 'Incorrect code. Please check your code and try again.' });
        }
      } catch (error) {
        this.otpInput = '';
        this.$root.$emit('raise_alert', { color: 'error', text: 'Network error. Please try again.' });
      } finally {
        this.sendingMultiFactor = false;
      }
    },
    async submitForm() {
      try {
        // Trim phone number to avoid saving empty spaces from formatting
        const formData = {
          ...this.form,
          phone: this.form.phone?.trim() || '',
        };
        
        const response = await DataService.createAccount(this.clientId, formData, this.userAuthToken);
        
        if (response.status === 201) {
          this.redirecting = true;
          const message = response.data.isNewPatient 
            ? 'Account created successfully! Redirecting you to login...' 
            : 'Welcome back! We\'ve linked your existing patient record. Redirecting you to login...';
          
          this.$root.$emit('raise_alert', { color: 'success', text: message });
          
          setTimeout(() => {
            this.$router.push({
              name: 'login',
              query: { username: this.form.email },
            });
          }, ALERT_TIMEOUT_MS);

          return;
        } else {
          const message = response.data?.replaceAll ? response.data : 'Something went wrong';
          this.$root.$emit('raise_alert', { color: 'error', text: message });
        }
      } catch (error) {
        this.$root.$emit('raise_alert', { color: 'error', text: 'Network error. Please try again.' });
      } finally {
        // Only hide overlay if not successful (success keeps it up for redirect)
        if (!this.redirecting) {
          this.creatingAccount = false;
        }
      }
    },
    async checkForm() {
      if (this.creatingAccount) return;
      
      if (!this.$refs.form || !this.$refs.form.validate()) {
        return;
      }
      
      this.creatingAccount = true;
      this.redirecting = false;
      
      try {
        await this.submitForm();
      } catch (error) {
        this.$root.$emit('raise_alert', { color: 'error', text: 'An error occurred. Please try again.' });
        this.creatingAccount = false;
      }
    },
    async getClients() {
      const clientsResponse = await DataService.getAvailableClients();
      this.clientList = clientsResponse.data.map((el) => {
        return {
          name: el.name,
          value: el.id,
        };
      });
    },
    async handleClientSelection() {
      await loadClientPreferences(this);
      await this.getClientReferralMethods();
    },
    async getClientReferralMethods() {
      const referralMethods = await DataService.getClientReferralMethods(this.clientId);
      this.referralList = referralMethods.data;
    },
    handlePatientSearchResults(data) {
      if (data.length === 1) {
        this.currentStep = 3;
        this.patientIdToLink = data[0].patientId;
        this.beginPatientValidation();
      } else if (data.length > 0) {
        this.currentStep = 2;
        this.potentialPatientMatches = data;
      } else {
        this.$root.$emit('raise_alert', { color: 'error', text: `No matching patient found. Please double-check your information and try again, or contact the ${this.clientName} office for assistance.` });
      }
    },
    validateForm() {
      const errors = [];
      const requiredFields = ['firstName', 'lastName', 'email', 'phone', 'password', 'confirmPassword'];
      
      requiredFields.forEach(field => {
        if (!this.form[field] || this.form[field].trim() === '') {
          errors.push(`${field} is required`);
        }
      });
      
      if (this.otherSelected && (!this.form.referralNote || this.form.referralNote.trim() === '')) {
        errors.push('Referral note is required');
      }
      
      return errors;
    },
  },
  async mounted() {
    try {
      const storedClientId = this.$route.query.clientId || DataService.getClientId();
      
      if (!storedClientId || storedClientId.startsWith('00000000')) {
        this.currentStep = 0;
        await this.getClients();
      } else {
        this.hadClientId = true;
        this.clientId = storedClientId;
        
        await Promise.all([
          loadClientPreferences(this),
          this.getClientReferralMethods(),
        ]);
      }
    } catch (error) {
      this.$root.$emit('raise_alert', { color: 'error', text: 'Failed to initialize. Please refresh the page.' });
    }
  },
};
</script>
